import "@szhsin/react-menu/dist/index.css";

import { Box } from "@material-ui/core";
import {
    FocusableItem,
    MenuDivider,
    MenuGroup,
    MenuItem,
    SubMenu,
} from "@szhsin/react-menu";
import React, { useContext, useRef, useState } from "react";
import { useStylesForButton, useStylesForMenu } from "shared/styles/makeStyles";

import Add from "shared/components/utils/icons/Add";
import { Button } from "react-bootstrap";
import CaretRight from "shared/components/utils/icons/CaretRight";
import Clock from "shared/components/utils/icons/Clock";
import Confirmation from "shared/components/feedback/modals/common/Confirmation";
import Context from "features/allocations/contexts/Context";
import CustomGroupContext from "features/allocations/contexts/CustomGroupContext";
import CustomGroupModal from "features/allocations/components/modals/CustomGroup";
import Delete from "shared/components/utils/icons/Delete";
import EmployeeDrawer from "./EmployeeDrawer";
import EmployeeGridSelectionContext from "features/allocations/contexts/EmployeeGridSelectionContext";
import External from "shared/components/utils/icons/External";
import FilterContext from "features/allocations/contexts/FilterContext";
import GenericModal from "shared/components/feedback/modals/common/GenericModal";
import GenericSidebarPortal from "shared/components/feedback/modals/common/GenericSidebarPortal";
import Link from "shared/components/navigation/links/Link";
import Loader from "shared/components/feedback/loading/Loader";
import MoreMenu from "shared/components/inputs/menu/MoreMenu";
import Profile from "shared/components/utils/icons/Profile";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import Tabs from "@material-ui/core/Tabs";
import Text from "shared/components/data-display/mui/Text";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import { batchOperation } from "shared/constants/allocationsEnums";
import { makeStyles } from "@material-ui/core/styles";
import { sendNotification } from "shared/components/feedback/notification/notification";
import { truncateText } from "shared/helper-functions/string/truncateText";
import { useAllocationsVisibilityOfUser } from "features/allocations/hooks/useAllocationsPermission";
import { useCustomGroups } from "features/allocations/api/useCustomGroups";
import { useErrors } from "shared/hooks/errors/useErrors";
import usePageActionPermission from "shared/hooks/access/usePageActionPermission";
import { useSkillSearchProfileUrlByEmployeeIdGetter } from "features/allocations/api/useSkillSearch";
import { without } from "lodash";
import {
    BlackSpan,
    MenuTab,
    SearchInput,
    LoaderWrapper,
    ArrowWrapper,
} from "./More.styles";

const useStyles = makeStyles(() => ({
    root: {
        display: "none",
        "& .MuiIconButton-root": {
            padding: 0,
            margin: 0,
        },
    },
    moreMenu: {
        maxHeight: "15rem",
        border: globalThis.config.border.medium,
        borderRadius: "0.18rem",
        backgroundColor: globalThis.config.colors.white,
    },
    itemLabel: {
        paddingRight: "16px",
    },
    noPadding: { padding: "0!important" },
    subMenu: {
        "&.rc-menu": {
            border: globalThis.config.border.medium,
            minWidth: "fit-content",
            padding: 0,
            zIndex: 1070,
            paddingTop: "0",
            paddingBottom: "0.5rem",
        },
        "& > .rc-menu__group": {
            overflow: "auto",
            maxHeight: "10.625rem",
        },
    },
}));

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

export default function More(props) {
    const {
        addUserToCustomGroup,
        removeUserFromCustomGroup,
        getAllCustomGroups,
    } = useCustomGroups();
    const classesMenu = useStylesForMenu();
    const [customGroups, setCustomGroups] = useState(null);
    const classes = useStyles();
    const customGroupModalRef = useRef();
    const [skillSearchProfileLink, setSkillSearchProfileLink] = useState(null);
    const buttonClasses = useStylesForButton();
    const customGroupConsumer = useContext(CustomGroupContext);
    const employeeGridSelectionConsumer = useContext(
        EmployeeGridSelectionContext
    );
    const filterConsumer = useContext(FilterContext);
    const getSkillSearchUrl = useSkillSearchProfileUrlByEmployeeIdGetter();
    const { handleError } = useErrors();
    const [isLoadingData, setIsLoadingData] = useState(false);
    const contextConsumer = useContext(Context);
    const hasCustomGroupPermission = usePageActionPermission(
        "AC_ADD_EDIT_VIEW_CUSTOM_GROUP"
    );
    const [drawerUserId, setDrawerUserId] = useState();
    const canViewAllocations = useAllocationsVisibilityOfUser(
        props.employee.userId
    );

    const getSkillSearchProfileUrl = async () => {
        if (!props.employee.userId) {
            return false;
        }

        if (props.skillSearchLink) {
            setSkillSearchProfileLink(props.skillSearchLink);
        } else {
            const url = await getSkillSearchUrl(props.employee.userId);
            setSkillSearchProfileLink(url);
        }
    };

    const openAddCustomGroupModal = () => {
        customGroupModalRef.current.add(props.employee);
    };

    const skipDuplicates = (currentCustomGroup, duplicatedUsers) => {
        const employeeIds = getIdsFromNonDuplicatedEmployees(duplicatedUsers);

        addUsersToCustomGroup(currentCustomGroup, employeeIds, true);
    };

    const getCustomGroups = async () => {
        setIsLoadingData(true);
        const r = await getAllCustomGroups();
        setCustomGroups(r);
        setIsLoadingData(false);
    };

    const addingUserNotification = (customGroup, submitedEmployeeIds) =>
        messageNotification(
            customGroup,
            submitedEmployeeIds,
            batchOperation.add
        );

    const messageNotification = (
        customGroup,
        submitedEmployeeIds,
        operation
    ) => {
        const allSelected = employeeGridSelectionConsumer.allSelected;

        const employeeId = props.employee.userId ?? props.employee.id;
        const employeeFullName = props.employee.fullName ?? props.employee.name;

        const selectedNames =
            employeeGridSelectionConsumer.getNamesFromSelectedIDs({
                idToExclude: employeeId,
            });

        const employeeNames = [employeeFullName, ...selectedNames];

        const listLength = submitedEmployeeIds.length;
        const othersLabel = listLength > 2 ? "others" : "other";

        return (
            <>
                <b>
                    {employeeNames[0]}&nbsp;
                    {listLength > 1 && (
                        <>
                            <span>and </span>
                            <Tooltip
                                snackbar={true}
                                placement="top"
                                title={employeeNames.slice(1).map((name) => (
                                    <Text key={name} variant="xsmall">
                                        {name}
                                    </Text>
                                ))}
                            >
                                <Button
                                    variant="link"
                                    className={buttonClasses.tooltipLink}
                                    style={{
                                        color: globalThis.config.colors.white,
                                        fontSize:
                                            globalThis.config.fonts.sizes.small,
                                    }}
                                >
                                    {allSelected
                                        ? "all filter result employees"
                                        : `${listLength - 1} ${othersLabel}`}
                                </Button>
                            </Tooltip>
                        </>
                    )}
                </b>{" "}
                {listLength === 1 ? "has" : "have"} been
                {operation === batchOperation.add
                    ? " added to "
                    : " removed from "}
                <b>{customGroup.Name}</b>.&nbsp;&nbsp;
                {operation === batchOperation.add && (
                    <Link
                        variant="secondary"
                        color="White"
                        href={void 0}
                        onClick={(event) => {
                            customGroupConsumer.selectCustomGroupOption(
                                event,
                                customGroup
                            );
                            handleDrawerClose();
                        }}
                    >
                        <b>View Group</b>
                    </Link>
                )}
            </>
        );
    };

    const addUsersToCustomGroup = async (customGroup, employeeIds, skip) => {
        if (!employeeIds.length) return;

        try {
            await addUserToCustomGroup(customGroup.Id, employeeIds, skip);
            sendNotification(addingUserNotification(customGroup, employeeIds));
            employeeGridSelectionConsumer.setSelectionIsEnabled(false);
            employeeGridSelectionConsumer.deselectAllEmployeeIDs();
            if (
                customGroupConsumer?.selectedCustomGroup?.Id === customGroup.Id
            ) {
                UpdateCustomGroup(employeeIds, batchOperation.add);
            }
        } catch (e) {
            if (e.status === 500 && e.detail?.["Duplicated users"]) {
                const duplicatedUsers = e.detail["Duplicated users"];

                // all employees selected are already in the custom group
                if (employeeIds.length === duplicatedUsers.length) {
                    const message =
                        employeeIds.length === 1
                            ? "The selected employee is already in this Custom Group."
                            : "All selected employees are already in this Custom Group.";

                    sendNotification(message);
                } else {
                    showDuplicatedUsersModal(customGroup, duplicatedUsers);
                }
                return;
            }

            handleError(e);
        }
    };

    const UpdateCustomGroup = (employeeIds, operation) => {
        const selectedGroup = { ...customGroupConsumer.selectedCustomGroup };
        selectedGroup.LastUpdateDateTime = new Date().toLocaleString();
        if (operation === batchOperation.add) {
            employeeIds.map((id) => selectedGroup.EmployeeIds.push(id));
            customGroupConsumer.selectCustomGroupOption(null, selectedGroup);
        } else {
            selectedGroup.EmployeeIds =
                customGroupConsumer.selectedCustomGroup.EmployeeIds.filter(
                    (e) => !employeeIds.includes(e)
                );

            let currentEmployees = { ...contextConsumer.employees };
            const updatedEmployees = Object.values(currentEmployees)
                .map((result) => {
                    return result.filter(
                        (employee) => !employeeIds.includes(employee.userId)
                    );
                })
                .filter((r) => r.length > 0);

            contextConsumer.setEmployees(updatedEmployees);
        }

        customGroupConsumer.setSelectedCustomGroup(selectedGroup);
    };

    const removeFromCustomGroup = async (_selectedAll, _summaryQuery) => {
        const employeeIds =
            employeeGridSelectionConsumer?.selectedEmployeeIDs.length > 0
                ? employeeGridSelectionConsumer.selectedEmployeeIDs
                : [props.employee.userId];

        let customGroup = { ...customGroupConsumer.selectedCustomGroup };

        if (employeeIds.length) {
            try {
                await removeUserFromCustomGroup(customGroup.Id, employeeIds);
                sendNotification(
                    removeUserNotification(customGroup, employeeIds)
                );
                handleDrawerClose();
                UpdateCustomGroup(employeeIds, batchOperation.remove);

                employeeGridSelectionConsumer.setSelectionIsEnabled(false);
                employeeGridSelectionConsumer.deselectAllEmployeeIDs();
            } catch {
                /**/
            }
        }
    };

    const addCustomGroup = (
        <>
            <Add size="16" color={globalThis.config.colors.black} />
            <span className={classes.itemLabel}>Add To Custom Group</span>
            <ArrowWrapper className="sub_menu_arrow">
                <CaretRight />
            </ArrowWrapper>
        </>
    );

    const removeUserNotification = (customGroup, submitedEmployeeIds) =>
        messageNotification(
            customGroup,
            submitedEmployeeIds,
            batchOperation.remove
        );

    function getIdsFromNonDuplicatedEmployees(duplicatedUsers) {
        let deduplicatedSelectedIds = [];
        const duplicatedIds = duplicatedUsers.map((u) => Number.parseInt(u.Id));

        if (employeeGridSelectionConsumer?.selectedEmployeeIDs.length > 0) {
            deduplicatedSelectedIds = without(
                employeeGridSelectionConsumer.selectedEmployeeIDs,
                duplicatedIds
            );
        } else if (
            duplicatedIds.includes(Number.parseInt(props.employee.userId))
        ) {
            deduplicatedSelectedIds = [props.employee.userId];
        }

        return deduplicatedSelectedIds;
    }

    const addToCustomGroup = async (currentCustomGroup) => {
        if (employeeGridSelectionConsumer?.selectedEmployeeIDs.length > 0) {
            addUsersToCustomGroup(
                currentCustomGroup,
                employeeGridSelectionConsumer.selectedEmployeeIDs
            );
        } else {
            const id = props.employee.id ?? props.employee.userId;
            addUsersToCustomGroup(currentCustomGroup, [id]);
        }
    };

    const handleDrawerClose = () => {
        setDrawerUserId(null);
    };

    const handleViewProfile = () => {
        if (!props.disableProfile) {
            setDrawerUserId(props.employee.userId);
        }
    };

    const showDuplicatedUsersModal = (currentCustomGroup, duplicatedUsers) => {
        const modalContent = (
            <Confirmation
                title="Duplicate Employee"
                onConfirm={() =>
                    skipDuplicates(currentCustomGroup, duplicatedUsers)
                }
                buttonLabel="Yes, Skip Duplicates"
            >
                {currentCustomGroup && duplicatedUsers.length && (
                    <>
                        <BlackSpan>
                            {duplicatedUsers[0]?.name}&nbsp;
                            {duplicatedUsers.length > 1 && (
                                <>
                                    <span>and </span>
                                    <Tooltip
                                        placement="top"
                                        title={duplicatedUsers
                                            .filter((index) => index !== 0)
                                            .map((u) => (
                                                <Text
                                                    key={u.Id}
                                                    variant="xsmall"
                                                >
                                                    {u.name}
                                                </Text>
                                            ))}
                                    >
                                        <Button
                                            variant="link"
                                            className={[
                                                buttonClasses.tooltipLink,
                                                buttonClasses.medium,
                                            ]}
                                        >
                                            {duplicatedUsers.length - 1}{" "}
                                            {duplicatedUsers.length - 1 === 1
                                                ? "other"
                                                : "others"}
                                        </Button>
                                    </Tooltip>
                                    &nbsp;
                                </>
                            )}
                        </BlackSpan>
                        {duplicatedUsers.length === 1 ? "is" : "are"} already in
                        the Custom Group:{" "}
                        <BlackSpan> {currentCustomGroup.Name}</BlackSpan>
                    </>
                )}
            </Confirmation>
        );

        const modal = (
            <GenericModal content={modalContent} parent="divPageModal" />
        );
        ReactDOM.render(modal, document.getElementById("divPageModal"));
    };

    const removeItemVisibility = () => {
        return (
            !customGroupConsumer?.selectedCustomGroup?.EmployeeIds ||
            Object.values(
                customGroupConsumer?.selectedCustomGroup?.EmployeeIds
            ).filter((id) => id === props.employee.userId).length === 0
        );
    };
    const tabs = ["My Groups", "All Groups"];

    const MenuTabItems = tabs.map((itemLabel, index) => (
        <MenuTab
            style={{
                fontSize: "0.75rem",
                textTransform: "none",
                minWidth: "7.031rem",
            }}
            key={itemLabel}
            label={itemLabel}
            {...a11yProps(index)}
        />
    ));

    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
    };

    const [tabValue, setTabValue] = useState(0);
    const [filter, setFilter] = useState("");

    const menuOptions = [
        <MenuItem
            key={0}
            hideItem={
                employeeGridSelectionConsumer?.selectedEmployeeIDs.length > 0 ||
                !props.employee.userId
            }
            onClick={() => {
                handleViewProfile();
            }}
            disabled={props.disableProfile}
        >
            <Profile
                color={
                    props.disableProfile
                        ? globalThis.config.colors.gray2
                        : globalThis.config.colors.black
                }
            />
            View Profile
        </MenuItem>,
        <MenuItem
            key={1}
            hideItem={
                employeeGridSelectionConsumer?.selectedEmployeeIDs.length > 0 ||
                !props.employee.userId ||
                !canViewAllocations
            }
        >
            <Link
                href={`${globalThis.config.routes.allocation}?userId=${props.employee.userId}`}
                target="_blank"
                rel="noreferrer"
                noHover
                noUnderline
            >
                <Clock />
                View Employee Allocations
            </Link>
        </MenuItem>,
        <MenuItem
            key={2}
            hideItem={
                employeeGridSelectionConsumer?.selectedEmployeeIDs.length > 0 ||
                !props.employee.userId
            }
            disabled={!skillSearchProfileLink}
        >
            <Link
                href={skillSearchProfileLink}
                target="_blank"
                rel="noreferrer"
                noHover
                noUnderline
                disabled={!skillSearchProfileLink}
            >
                <External
                    color={
                        skillSearchProfileLink
                            ? globalThis.config.colors.black
                            : globalThis.config.colors.gray2
                    }
                />
                View Skill Search Profile
            </Link>
        </MenuItem>,
        <SubMenu
            key={3}
            label={addCustomGroup}
            offsetY={-3}
            offsetX={3}
            overflow="auto"
            position="anchor"
            className={classes.subMenu}
            hideItem={!hasCustomGroupPermission}
        >
            <MenuItem
                onClick={(e) => (e.keepOpen = true)}
                className={classes.noPadding}
            >
                <Box
                    className={classesMenu.root}
                    variant="smallLight"
                    style={{ width: "225px" }}
                >
                    <Tabs
                        value={tabValue}
                        onChange={handleChangeTab}
                        aria-label="simple tabs example"
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                    >
                        {MenuTabItems}
                    </Tabs>
                </Box>
            </MenuItem>
            <FocusableItem className={classes.noPadding}>
                {({ ref }) => (
                    <SearchInput
                        ref={ref}
                        type="text"
                        placeholder="Search"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className={classesMenu.searchInput}
                        style={{
                            fontSize: "0.7rem",
                            textTransform: "none",
                        }}
                    />
                )}
            </FocusableItem>
            {isLoadingData ? (
                <MenuItem onClick={(e) => (e.keepOpen = true)}>
                    <LoaderWrapper>
                        <Loader height="1rem" width="1rem" />
                    </LoaderWrapper>
                </MenuItem>
            ) : (
                <MenuGroup takeOverflow>
                    {customGroups
                        ?.filter(
                            (f) =>
                                f.Name.toUpperCase().includes(
                                    filter.trim().toUpperCase()
                                ) &&
                                ((tabValue === 0 && f.Own) ||
                                    (tabValue === 1 && (f.Own || !f.Own)))
                        )
                        .map((item) => (
                            <MenuItem
                                key={item.Id}
                                onClick={() => {
                                    addToCustomGroup(item);
                                }}
                            >
                                {truncateText(item.Name, 200)}
                            </MenuItem>
                        ))}
                </MenuGroup>
            )}
            <MenuDivider />
            <MenuItem
                key={4}
                onClick={openAddCustomGroupModal}
                style={{ zIndex: "2000" }}
            >
                <Add size="16" color={globalThis.config.colors.black} />
                Create Custom Group
            </MenuItem>
        </SubMenu>,
        <MenuItem
            key={5}
            hideItem={!hasCustomGroupPermission || removeItemVisibility()}
            onClick={() => {
                removeFromCustomGroup(
                    employeeGridSelectionConsumer?.allSelected,
                    filterConsumer.summaryQuery
                );
            }}
        >
            <Delete size="16" viewBox="16" />
            Remove from this Custom Group
        </MenuItem>,
    ];

    return (
        <>
            <MoreMenu
                id={props.id}
                alwaysVisible={props.visible}
                onOpen={() => [getCustomGroups(), getSkillSearchProfileUrl()]}
                direction={props.direction}
                portal={props.portal}
                position="initial"
            >
                {menuOptions}
            </MoreMenu>
            <CustomGroupModal ref={customGroupModalRef} />

            {drawerUserId && (
                <Context.Provider value={contextConsumer}>
                    <CustomGroupContext.Provider value={customGroupConsumer}>
                        <GenericSidebarPortal
                            open={true}
                            content={<EmployeeDrawer userId={drawerUserId} />}
                            onClose={handleDrawerClose}
                        />
                    </CustomGroupContext.Provider>
                </Context.Provider>
            )}
        </>
    );
}

More.propTypes = {
    employee: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    skillSearchLink: PropTypes.string,
    disableProfile: PropTypes.bool,
    id: PropTypes.string,
    visible: PropTypes.bool,
    direction: PropTypes.string,
    portal: PropTypes.bool,
};
